import {
  keepPreviousData,
  mutationOptions,
  queryOptions,
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { type QueryError } from 'src/queries/network.utils'

import {
  archiveVendor,
  completeVendorAccountCollectionFlow,
  confirmVendorAccountCollectionFlow,
  createVendor,
  createVendorAccountCollectionFlow,
  getVendor,
  getVendorPaymentMethodDetails,
  getVendors,
  retireVendorPaymentMethod,
  updateVendorDetails,
} from './vendors.network'
import {
  ArchiveVendorMutationInput,
  CompleteVendorAccountCollectionFlowInput,
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorMutationInput,
  ListVendorsParams,
  RetireVendorPaymentMethodInput,
  UpdateVendorDetailsMutationInput,
  Vendor,
  VendorAccountCollectionFlowCompletionResponse,
  VendorAccountCollectionFlowConfirmationResponse,
  VendorAccountCollectionFlowResponse,
} from './vendors.types'

export const vendorKeys = {
  lists: (organizationId: string | undefined) =>
    ['vendors', 'list', organizationId] as const,
  list: (organizationId: string | undefined, params: ListVendorsParams) =>
    [...vendorKeys.lists(organizationId), params] as const,
  details: (organizationId: string | undefined) =>
    ['vendors', 'detail', organizationId] as const,
  detail: (organizationId: string | undefined, vendorId: string | undefined) =>
    [...vendorKeys.details(organizationId), vendorId] as const,
  // Own family root: a vendor's payment-method bank details are fetched live
  // from Modern Treasury and are not always safe to refetch alongside the
  // vendor detail, so they get a distinct first-segment family
  // (`src/queries/AGENTS.md`).
  paymentMethods: (organizationId: string | undefined) =>
    ['vendors', 'payment-method', organizationId] as const,
  paymentMethodDetail: (
    organizationId: string | undefined,
    vendorId: string | undefined,
    paymentMethodId: string | undefined,
  ) =>
    [
      ...vendorKeys.paymentMethods(organizationId),
      vendorId,
      paymentMethodId,
    ] as const,
}

export const vendorQueries = {
  list: (organizationId: string | undefined, params: ListVendorsParams) =>
    queryOptions({
      queryKey: vendorKeys.list(organizationId, params),
      queryFn: () => getVendors(params),
      enabled: Boolean(organizationId),
      /**
       * Search and paging both change `params`, so every keystroke mints a new
       * query key with no cached entry. Without this the list would blank out
       * between results; keepPreviousData holds the prior rows visible while
       * the next request resolves. Same no-flash treatment the other paged
       * lists use (expenseReports, virtualCards, users).
       */
      placeholderData: keepPreviousData,
    }),
  detail: (organizationId: string | undefined, vendorId: string | undefined) =>
    queryOptions({
      queryKey: vendorKeys.detail(organizationId, vendorId),
      queryFn: () => getVendor(vendorId!),
      enabled: Boolean(organizationId && vendorId),
    }),
  /**
   * A vendor payment method's display-safe bank details. This data is fetched
   * live from Modern Treasury and is never persisted, so it is treated as
   * realtime (default staleTime — refetch on mount) rather than cached-forever.
   * The `queryFn` guards both ids so a caller `enabled` override can never fire
   * a malformed request.
   */
  paymentMethodDetails: (
    organizationId: string | undefined,
    vendorId: string | undefined,
    paymentMethodId: string | undefined,
  ) =>
    queryOptions({
      queryKey: vendorKeys.paymentMethodDetail(
        organizationId,
        vendorId,
        paymentMethodId,
      ),
      queryFn: () => getVendorPaymentMethodDetails(vendorId!, paymentMethodId!),
      enabled: Boolean(organizationId && vendorId && paymentMethodId),
    }),
}

export const vendorMutations = {
  create: () =>
    mutationOptions<Vendor, QueryError, CreateVendorMutationInput>({
      mutationFn: (input) => createVendor(input),
    }),
  updateDetails: () =>
    mutationOptions<Vendor, QueryError, UpdateVendorDetailsMutationInput>({
      mutationFn: (input) => updateVendorDetails(input),
    }),
  archive: () =>
    mutationOptions<Vendor, QueryError, ArchiveVendorMutationInput>({
      mutationFn: (input) => archiveVendor(input),
    }),
  createAccountCollectionFlow: () =>
    mutationOptions<VendorAccountCollectionFlowResponse, QueryError, string>({
      mutationFn: (vendorId) => createVendorAccountCollectionFlow(vendorId),
    }),
  completeAccountCollectionFlow: () =>
    mutationOptions<
      VendorAccountCollectionFlowCompletionResponse,
      QueryError,
      CompleteVendorAccountCollectionFlowInput
    >({
      mutationFn: (input) => completeVendorAccountCollectionFlow(input),
    }),
  confirmAccountCollectionFlow: () =>
    mutationOptions<
      VendorAccountCollectionFlowConfirmationResponse,
      QueryError,
      ConfirmVendorAccountCollectionFlowInput
    >({
      mutationFn: (input) => confirmVendorAccountCollectionFlow(input),
    }),
  retirePaymentMethod: () =>
    mutationOptions<Vendor, QueryError, RetireVendorPaymentMethodInput>({
      mutationFn: (input) => retireVendorPaymentMethod(input),
    }),
}

type CreateVendorMutationOptions = Omit<
  UseMutationOptions<Vendor, QueryError, CreateVendorMutationInput>,
  'mutationFn'
>

type UpdateVendorDetailsMutationOptions = Omit<
  UseMutationOptions<Vendor, QueryError, UpdateVendorDetailsMutationInput>,
  'mutationFn'
>

type ArchiveVendorMutationOptions = Omit<
  UseMutationOptions<Vendor, QueryError, ArchiveVendorMutationInput>,
  'mutationFn'
>

type ConfirmVendorMutationOptions = Omit<
  UseMutationOptions<
    VendorAccountCollectionFlowConfirmationResponse,
    QueryError,
    ConfirmVendorAccountCollectionFlowInput
  >,
  'mutationFn'
>

type RetireVendorPaymentMethodMutationOptions = Omit<
  UseMutationOptions<Vendor, QueryError, RetireVendorPaymentMethodInput>,
  'mutationFn'
>

export function useCreateVendor(
  organizationId: string,
  options?: CreateVendorMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.create(),
    ...restOptions,
    onSuccess: (vendor, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, vendor.id),
        exact: true,
      })
      onSuccess?.(vendor, variables, onMutateResult, context)
    },
  })
}

/**
 * Edit vendor Step-1 details (EX-40771). Same invalidation contract as
 * {@link useCreateVendor}: refresh the vendor lists and the one exact detail so
 * the table and any open flyout reflect the saved values, then chain the
 * caller's `onSuccess` (toast + close). Invalidation lives on the mutation
 * callback (survives the flyout unmounting on close); the toast/close side
 * effects stay on the caller's `mutate(...)` per src/queries/AGENTS.md.
 */
export function useUpdateVendorDetails(
  organizationId: string,
  options?: UpdateVendorDetailsMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.updateDetails(),
    ...restOptions,
    onSuccess: (vendor, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
      onSuccess?.(vendor, variables, onMutateResult, context)
    },
  })
}

/**
 * Archive a vendor, then refresh every organization-scoped list and the exact
 * detail entry. List invalidation is required because the backend reorders
 * archived vendors after active vendors; optimistic placement would duplicate
 * that server-owned ordering logic.
 */
export function useArchiveVendor(
  organizationId: string,
  options?: ArchiveVendorMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.archive(),
    ...restOptions,
    onSuccess: (vendor, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
      onSuccess?.(vendor, variables, onMutateResult, context)
    },
  })
}

export function useCreateVendorAccountCollectionFlow() {
  return useMutation(vendorMutations.createAccountCollectionFlow())
}

export function useCompleteVendorAccountCollectionFlow(organizationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    ...vendorMutations.completeAccountCollectionFlow(),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
    },
  })
}

export function useVendorDetail(
  organizationId: string | undefined,
  vendorId: string | undefined,
) {
  return useQuery(vendorQueries.detail(organizationId, vendorId))
}

export function useConfirmVendorAccountCollectionFlow(
  organizationId: string,
  options?: ConfirmVendorMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.confirmAccountCollectionFlow(),
    ...restOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
      onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}

/**
 * Retire a vendor's current ACH payment method (EX-40772). The retire is a real
 * state change on the vendor, so refresh three families: the org lists and the
 * exact vendor detail (the vendor flips to Action Required — its `paymentMethod`
 * key is now absent), AND the `paymentMethodDetail` family, which is a separate
 * first-segment key that a vendor-detail invalidation does not reach
 * (`src/queries/AGENTS.md`). Invalidation lives on the mutation callback so it
 * survives the flyout unmounting on close; the toast/close side effects stay on
 * the caller's `mutate(...)`. Nothing is invalidated on failure.
 */
export function useRetireVendorPaymentMethod(
  organizationId: string,
  options?: RetireVendorPaymentMethodMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.retirePaymentMethod(),
    ...restOptions,
    onSuccess: (vendor, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.paymentMethodDetail(
          organizationId,
          variables.vendorId,
          variables.paymentMethodId,
        ),
      })
      onSuccess?.(vendor, variables, onMutateResult, context)
    },
  })
}
